import { Routes, Route } from 'react-router-dom';
import { OptimizedApp } from './components/app/OptimizedApp';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorNotificationProvider, useErrorNotification } from './contexts/ErrorNotificationContext';
import { ScrollRevealProvider } from './contexts/ScrollRevealContext';
import { SmoothScrollProvider } from './contexts/SmoothScrollContext';
import { Navigation } from './components/Navigation';
import { HomePage } from './pages/HomePage';
import { GalleryPage } from './pages/GalleryPage';
import { ArtistsPage } from './pages/ArtistsPage';
import { StudentArtworkPage } from './pages/StudentArtworkPage';
import { ExhibitionsPage } from './pages/ExhibitionsPage';
import { AboutPage } from './pages/AboutPage';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import ErrorNotificationDisplay from './components/ui/ErrorNotification';
import { deviceDetection } from './utils/mobileUtils';
import { ErrorBoundary } from './components/ui';
import { theme } from './styles/theme';

const NavFallback = () => (
  <nav style={{ background: theme.colors.semantic.background, padding: '1rem', position: 'fixed', width: '100%', top: 0, zIndex: 100 }}>
    <a href="/" style={{ color: theme.colors.semantic.textPrimary, fontWeight: 'bold' }}>Joanne Art Class</a>
  </nav>
);

function AppContent() {
  const { showAuthError } = useErrorNotification();
  const isMobile = deviceDetection.isMobile();
  const handleAuthError = (error: { message: string; type: string }) => {
    if (error.type === 'auth') {
      showAuthError(error.message);
    }
  };

  const appContent = (
    <div className="app">
      <ErrorBoundary fallback={<NavFallback />}>
        <Navigation />
      </ErrorBoundary>
      <main className="public-shell">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/artists" element={<ArtistsPage />} />
          <Route path="/artists/:studentId" element={<StudentArtworkPage />} />
          <Route path="/exhibitions" element={<ExhibitionsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route 
            path="/admin/login" 
            element={
              <PublicRoute>
                <AdminLoginPage />
              </PublicRoute>
            } 
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <ErrorNotificationDisplay />
    </div>
  );

  return (
    <OptimizedApp
      enableTouchOptimization={true}
      onAuthError={handleAuthError}
    >
      <AuthProvider onAuthError={handleAuthError}>
        {isMobile ? (
          // Mobile: Skip SmoothScrollProvider for better native scrolling performance
          <ScrollRevealProvider>
            {appContent}
          </ScrollRevealProvider>
        ) : (
          // Desktop: Use SmoothScrollProvider for enhanced scrolling experience
          <SmoothScrollProvider>
            <ScrollRevealProvider>
              {appContent}
            </ScrollRevealProvider>
          </SmoothScrollProvider>
        )}
      </AuthProvider>
    </OptimizedApp>
  );
}

function App() {
  return (
    <ErrorNotificationProvider>
      <AppContent />
    </ErrorNotificationProvider>
  );
}

export default App;
